import React, { useContext, useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import { COMPANY } from '../../constants/company';
import { authService } from '../../services/authService';
import {
  HomeIcon,
  BuildingOfficeIcon,
  DocumentTextIcon,
  ChartBarIcon,
  DocumentMagnifyingGlassIcon,
  CogIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  TicketIcon,
  UserCircleIcon,
  DocumentCheckIcon,
  VideoCameraIcon,
  FunnelIcon,
  HeartIcon,
  BellAlertIcon,
  GlobeAltIcon,
  EnvelopeIcon,
  UsersIcon,
  CurrencyPoundIcon,
} from '@heroicons/react/24/outline';

export default function SuperAdminLayout({ children }) {
  const { userData, loading } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Security check: Redirect non-super-admin users
  useEffect(() => {
    if (!loading && userData && userData.role !== 'super_admin') {
      console.warn('SuperAdminLayout: Unauthorized access attempt. User role:', userData.role);
      // Redirect to appropriate dashboard based on role
      if (userData.role === 'clinic_admin' || userData.role === 'staff') {
        navigate('/clinic', { replace: true });
      } else {
        navigate('/login', { replace: true });
      }
    }
  }, [userData, loading, navigate]);

  // Show loading while checking auth
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Don't render if user is not super admin
  if (!userData || userData.role !== 'super_admin') {
    return null;
  }

  const navigation = [
    { name: 'Dashboard', href: '/admin', icon: HomeIcon },
    { name: 'Practice Directory', href: '/admin/clinics', icon: BuildingOfficeIcon },
    { name: 'User Export', href: '/admin/user-export', icon: UsersIcon },
    { name: 'Registration Funnel', href: '/admin/registration-funnel', icon: FunnelIcon },
    { name: 'Clinic Engagement', href: '/admin/clinic-engagement', icon: BellAlertIcon },
    { name: 'Demo Invites', href: '/admin/demo-invites', icon: EnvelopeIcon },
    { name: 'Dental Triage', href: '/admin/dental-triage', icon: HeartIcon },
    { name: 'Consents', href: '/admin/consents', icon: DocumentCheckIcon },
    { name: 'Treatment Videos', href: '/admin/treatment-videos', icon: VideoCameraIcon },
    { name: 'Analytics', href: '/admin/analytics', icon: ChartBarIcon },
    { name: 'Revenue', href: '/admin/revenue', icon: CurrencyPoundIcon },
    { name: 'Website Traffic', href: '/admin/website-traffic', icon: GlobeAltIcon },
    { name: 'Support Tickets', href: '/admin/support', icon: TicketIcon },
    { name: 'Audit Logs', href: '/admin/logs', icon: DocumentMagnifyingGlassIcon },
    { name: 'Profile', href: '/admin/profile', icon: UserCircleIcon },
  ];

  const handleLogout = async () => {
    await authService.signOut();
    navigate('/login');
  };

  const isActive = (href) => {
    if (href === '/admin') return location.pathname === '/admin';
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile menu button */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="p-2 bg-white rounded-md shadow-md"
        >
          {mobileMenuOpen ? (
            <XMarkIcon className="h-6 w-6" />
          ) : (
            <Bars3Icon className="h-6 w-6" />
          )}
        </button>
      </div>

      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-40 w-64 bg-gray-900 transform transition-transform duration-300 ease-in-out ${
        mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
      } lg:translate-x-0`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="px-4 py-6 border-b border-gray-800">
            <h2 className="text-2xl font-bold text-white">Smile Consent</h2>
            <p className="text-sm text-gray-400 mt-1">Super Admin Portal</p>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-1">
            {navigation.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    isActive(item.href)
                      ? 'bg-gray-800 text-white'
                      : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Icon className="h-5 w-5 mr-3" />
                  {item.name}
                </Link>
              );
            })}
          </nav>

          {/* User info and logout */}
          <div className="border-t border-gray-800 px-4 py-4">
            <div className="mb-4">
              <p className="text-sm font-medium text-white">{userData?.displayName}</p>
              <p className="text-xs text-gray-400">{userData?.email}</p>
              <p className="text-xs text-gray-400 capitalize">Super Administrator</p>
            </div>
            <p className="mb-3 text-[11px] leading-relaxed text-gray-500">
              {COMPANY.legalName} · Company no. {COMPANY.number}
            </p>
            <button
              onClick={handleLogout}
              className="flex items-center w-full px-4 py-2 text-sm font-medium text-gray-300 hover:bg-gray-800 hover:text-white rounded-lg transition-colors"
            >
              <ArrowRightOnRectangleIcon className="h-5 w-5 mr-3" />
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="w-full min-w-0 lg:pl-64">
        <main className="min-h-screen w-full max-w-full">
          {children}
        </main>
      </div>

      {/* Mobile menu overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
    </div>
  );
}